<template>
  <div class="detail-card" data-card-id="ratify-analysis">
    <div
      class="card-header"
      :class="{ 'slidein-header': mode === 'draggable' }"
    >
      <div class="header-left">
        <h5 class="card-title">RATIFY Analysis</h5>
      </div>
      <div class="header-right">
        <button
          class="drag-toggle-btn"
          @click.prevent="$emit('toggleDraggable')"
          :title="mode === 'draggable' ? 'Dock to panel' : 'Make draggable'"
        >
          <font-awesome-icon
            :icon="mode === 'draggable' ? ['fas', 'times'] : ['fas', 'clone']"
          />
        </button>
      </div>
    </div>
    <div class="card-content">
      <div class="detail-item">
        <span class="detail-label">Created on:</span>
        <span class="detail-value">{{
          formatDateTime(image?.reportAnalysis?.created_at, "yyyy-MM-dd HH:mm")
        }}</span>
      </div>
      <div class="detail-item">
        <span class="detail-label">AI vs Report Variability:</span>
        <span class="detail-value">
          <b>{{ ((1 - image.reportAnalysis.accuracy) * 100).toFixed(2) }}%</b>
        </span>
      </div>
      <div class="analysis-conclusion">
        <p>{{ image.reportAnalysis.conclusion }}</p>
      </div>
      <div class="expandable-section">
        <a
          href="#"
          @click.prevent="reportAnalysisOpen = !reportAnalysisOpen"
          class="section-toggle"
        >
          <font-awesome-icon
            :icon="
              reportAnalysisOpen
                ? ['fas', 'chevron-down']
                : ['fas', 'chevron-right']
            "
            class="me-2"
          />
          <b>Details</b>
        </a>
        <div v-if="reportAnalysisOpen" class="analysis-details">
          <h6>
            AI Detections Analysis <span class="subtitle">(per image)</span>
          </h6>
          <div
            v-for="pathology in llmPathologies"
            :key="pathology._id"
            class="analysis-item"
          >
            <div class="analysis-icon">
              <font-awesome-icon
                v-if="pathology.confirmed"
                :icon="['fas', 'check-circle']"
                class="status-confirmed"
              />
              <font-awesome-icon
                v-else-if="pathology.partiallyConfirmed"
                :icon="['fas', 'check-circle']"
                class="status-partial"
              />
              <font-awesome-icon
                v-else
                :icon="['fas', 'times-circle']"
                class="status-rejected"
              />
            </div>
            <div class="analysis-content">
              <div class="analysis-name">
                {{ pathology.name }}
                <small
                  v-if="pathology.location && pathology.location.length > 0"
                >
                  ({{ pathology.location.join(", ") }})
                </small>
              </div>
              <div class="analysis-notes">{{ pathology.notes }}</div>
            </div>
          </div>

          <div
            v-if="
              image.reportAnalysis.falseNegatives?.filter(
                (fn) => fn.confirmed || fn.partiallyConfirmed
              ).length > 0
            "
            class="false-negatives"
          >
            <hr class="section-divider" />
            <h6>Vendor Missed AI Detections Analysis</h6>
            <div
              v-for="pathology in image.reportAnalysis.falseNegatives.filter(
                (fn) => fn.confirmed || fn.partiallyConfirmed
              )"
              :key="pathology._id"
              class="analysis-item"
            >
              <div class="analysis-icon">
                <font-awesome-icon
                  v-if="pathology.confirmed"
                  :icon="['fas', 'times-circle']"
                  class="status-rejected"
                />
                <font-awesome-icon
                  v-else-if="pathology.partiallyConfirmed"
                  :icon="['fas', 'times-circle']"
                  class="status-partial"
                />
                <font-awesome-icon
                  v-else
                  :icon="['fas', 'check-circle']"
                  class="status-confirmed"
                />
              </div>
              <div class="analysis-content">
                <div class="analysis-name">
                  {{ pathology.name }}
                  <small
                    v-if="pathology.location && pathology.location.length > 0"
                  >
                    ({{ pathology.location.join(", ") }})
                  </small>
                </div>
                <div class="analysis-notes">{{ pathology.notes }}</div>
              </div>
            </div>
          </div>

          <div
            v-if="image.reportAnalysis.aiDiagnosis"
            class="diagnosis-section"
          >
            <hr class="section-divider" />
            <h6>
              <font-awesome-icon
                v-if="image.reportAnalysis.aiDiagnosis.confirmed"
                :icon="['fas', 'check-circle']"
                class="status-partial me-2"
              />
              <font-awesome-icon
                v-else-if="image.reportAnalysis.aiDiagnosis.partiallyConfirmed"
                :icon="['fas', 'check-circle']"
                class="status-partial me-2"
              />
              <font-awesome-icon
                v-else-if="image.reportAnalysis.aiDiagnosis.confirmed === null"
                :icon="['fas', 'check-circle']"
                class="me-2"
              />
              <font-awesome-icon
                v-else
                :icon="['fas', 'times-circle']"
                class="status-rejected me-2"
              />
              Diagnosis
            </h6>
            <p>{{ image.reportAnalysis.aiDiagnosis.notes }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from "vue";
import { formatDateTime } from '@/helpers/project-management/formatters'

const props = defineProps({
  image: Object,
  llmPathologies: Array,
  mode: {
    type: String,
    default: "parked",
  },
});

defineEmits(["toggleDraggable"]);

const reportAnalysisOpen = ref(false);

</script>

<style scoped>
/* Analysis Section (migrated from global styles.css) */
.analysis-conclusion {
  margin-top: 1rem;
  padding: 1rem;
  background-color: var(--color-bg-primary);
  border-radius: var(--border-radius-small);
}

.analysis-conclusion h6 {
  color: var(--color-text-primary);
  font-size: 0.95rem;
  font-weight: var(--font-weight-bold);
  margin: 0 0 0.5rem 0;
}

.analysis-conclusion p {
  color: var(--color-text-primary);
  font-size: 0.9rem;
  line-height: 1.5;
  margin: 0;
}

.analysis-details {
  margin-top: 0.5rem;
}

.analysis-details h6 {
  color: var(--color-text-primary);
  font-size: 0.95rem;
  font-weight: var(--font-weight-bold);
  margin: 1rem 0 0.5rem 0;
}

.analysis-item {
  display: flex;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
  padding: 0.75rem;
  background-color: var(--color-bg-primary);
  border-radius: var(--border-radius-small);
}

.analysis-icon {
  font-size: 1rem;
  flex-shrink: 0;
}

.analysis-content {
  flex: 1;
}

.analysis-name {
  color: var(--color-text-primary);
  font-size: 0.9rem;
  font-weight: var(--font-weight-semibold);
  margin-bottom: 0.25rem;
}

.analysis-name small {
  color: var(--color-text-secondary);
  font-size: 0.85rem;
  font-weight: var(--font-weight-normal);
}

.analysis-notes {
  color: var(--color-text-secondary);
  font-size: 0.85rem;
  line-height: 1.4;
}

.status-confirmed {
  color: #28a745;
}

.status-partial {
  color: var(--color-accent-primary);
}

.status-rejected {
  color: var(--color-error);
}
</style>
